import React, { useEffect, useRef, useState } from "react";
import "./RequestStageDetail.scss";
import Filter from "../Filter/Filter";
import { FilterOptionSchema } from "../Filter/filter.data";
import URLS from "../../urls/Urls";
import ApiService from "../../services/apiService";
import WorkFlowAddAssigneeDialog from "../WorkFlowAddAssigneeDialog/WorkFlowAddAssigneeDialog";
import {
  IRequestStageDetail,
  WORKFLOW_REQUEST_STAGE_STATUS,
} from "../../models/WorkflowRequest";
import { useWorkFlowContext } from "../../context/workflow-context";

interface RequestStageDetailProps {
  request_id: number;
  stage_id: number;
}

const RequestStageDetail: React.FC<RequestStageDetailProps> = ({
  request_id,
  stage_id,
}) => {
  const { setSelectedNode } = useWorkFlowContext();
  const [details, setDetails] = useState<IRequestStageDetail>();
  const [noteEditing, setNoteEditing] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("");
  const [showAddFollowersDialog, setShowAddFollowersDialog] = useState(false);
  const [showAddAssigneeDialog, setShowAddAssigneeDialog] = useState(false);
  const noteRef = useRef<HTMLDivElement>(null);
  const [userList, setUserList] = useState<FilterOptionSchema[]>([]);

  useEffect(() => {
    getRequestStageDetail();
    getUserList();
  }, []);

  const getRequestStageDetail = async () => {
    const { data } = await ApiService().client.get(
      URLS.WORKFLOW.UPDATE_STAGE_DETAIL(request_id, stage_id),
    );
    setDetails(data.details);
    setCurrentStatus(data.details.status);
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setNoteEditing(false);
      if (noteRef.current) {
        const notes = noteRef.current.innerHTML.replace(/\u00A0/g, " ").trim();
        if (details?.notes === notes) {
          return;
        }
        const res = await ApiService().client.put(
          URLS.WORKFLOW.UPDATE_STAGE_DETAIL(request_id, stage_id),
          {
            note: noteRef.current.innerHTML.replace(/\u00A0/g, " ").trim(),
          },
        );
        if (res.status === 200) {
          setDetails((prev) => {
            if (prev) {
              return { ...prev, notes: res.data.result.notes };
            }
            return prev;
          });
        }
      }
    }
  };

  const getUserList = async () => {
    const { data } = await ApiService().client.get(
      URLS.WORKFLOW.REQUEST_USER_LIST,
    );
    setUserList(data.users);
  };

  const handleAssigneeSelection = async (assignee: FilterOptionSchema) => {
    if (!details) return;

    const isPresent = details.assignees.some((user) => user.id === assignee.id);
    let updatedAssignees;

    if (isPresent) {
      // Remove assignee
      updatedAssignees = details.assignees.filter(
        (user) => user.id !== assignee.id,
      );

      try {
        await ApiService().client.put(
          URLS.WORKFLOW.UPDATE_STAGE_DETAIL(request_id, stage_id),
          { remove_assignees: [assignee.id] },
        );
      } catch (error) {
        console.error("Error removing assignee:", error);
      }
    } else {
      // Add assignee
      updatedAssignees = [...details.assignees, assignee];
      setDetails({ ...details, assignees: updatedAssignees });

      try {
        const res = await ApiService().client.put(
          URLS.WORKFLOW.UPDATE_STAGE_DETAIL(request_id, stage_id),
          { new_assignees: [assignee.id] },
        );
        if (res.status === 200) {
          if (details.assigned_date) {
            setDetails({ ...details, assignees: updatedAssignees });
          } else {
            setDetails({
              ...details,
              assigned_date: res.data.result.assigned_date,
              due_date: res.data.result.due_date,
              assignees: updatedAssignees,
            });
          }
        }
      } catch (error) {
        console.error("Error adding assignee:", error);
      }
    }
  };

  const handleFollowerSelection = async (follower: FilterOptionSchema) => {
    if (!details) return;

    const isPresent = details.followers.some((user) => user.id === follower.id);
    let updatedFollowers;

    if (isPresent) {
      // Remove follower
      updatedFollowers = details.followers.filter(
        (user) => user.id !== follower.id,
      );
      setDetails({ ...details, followers: updatedFollowers });

      try {
        await ApiService().client.put(
          URLS.WORKFLOW.UPDATE_STAGE_DETAIL(request_id, stage_id),
          { remove_followers: [follower.id] },
        );
      } catch (error) {
        console.error("Error removing follower:", error);
      }
    } else {
      // Add follower
      updatedFollowers = [...details.followers, follower];
      setDetails({ ...details, followers: updatedFollowers });

      try {
        await ApiService().client.put(
          URLS.WORKFLOW.UPDATE_STAGE_DETAIL(request_id, stage_id),
          { new_followers: [follower.id] },
        );
      } catch (error) {
        console.error("Error adding follower:", error);
      }
    }
  };

  const handleNextStep = async (val: FilterOptionSchema[]) => {
    try {
      const res = await ApiService().client.put(
        URLS.WORKFLOW.REQUEST_STAGE_DETAIL(request_id, stage_id),
        { next_stage_id: val[0].id },
      );
      if (res.status === 200) {
        setCurrentStatus(WORKFLOW_REQUEST_STAGE_STATUS.COMPLETED);
        setDetails({
          ...(details as IRequestStageDetail),
          completed_date: res.data.result.completed_date,
        });
      }
    } catch (error) {
      console.error("Error Moving to Next Stage:", error);
    }
  };

  return (
    <>
      {details && (
        <div className="normal-node-container flex-column gap-16 p-16">
          <div className=" flex-row align-items-center justify-content-space-between xetgo-font-tag">
            <p
              style={{ color: "#333333" }}
              className="xetgo-font-button bolder"
            >
              {details.title}
            </p>
            <p
              className={`current-status px-12 py-4 ${currentStatus
                .toLowerCase()
                .split(" ")
                .join("-")}`}
            >
              {currentStatus}
            </p>
          </div>
          <div className=" flex-row gap-48 justify-content-space-between xetgo-font-tag">
            <p className="bold left-title">Description</p>
            <p style={{ color: "#333333" }} className="flex-1">
              {details.description}
            </p>
          </div>
          <div className=" flex-row gap-48 justify-content-space-between xetgo-font-tag">
            <p className="bold left-title">SLA</p>
            <p style={{ color: "#333333" }} className="flex-1">
              {details.sla} days
            </p>
          </div>
          <div className="mid-border"></div>
          <div className=" flex-row gap-48 justify-content-space-between xetgo-font-tag">
            <p className="bold left-title">Requester</p>
            <p style={{ color: "#333333" }} className="flex-1">
              {details.requestor}
            </p>
          </div>
          <div className=" flex-row gap-48 justify-content-space-between xetgo-font-tag">
            <p className="bold left-title">Team</p>
            <p style={{ color: "#333333" }} className="flex-1">
              {details.team}
            </p>
          </div>
          <div className="followers-main-container flex-row gap-48 justify-content-space-between xetgo-font-tag">
            <p className="bold left-title">Assigned to</p>
            <div className="flex-row gap-8 flex-1">
              <img
                onClick={() => setShowAddAssigneeDialog(true)}
                className="cursor-pointer"
                height={16}
                width={16}
                src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250403104959-user-plus.svg"
                alt="add-user-icon"
              />

              <div
                style={{ color: "#333333" }}
                className="flex-row gap-8 flex-wrap"
              >
                {details.assignees &&
                  details.assignees.map((assignee, index) => (
                    <p
                      className="flex-row align-items-center justify-content-center gap-4"
                      key={index}
                    >
                      <span className="dot-follower p-1"> </span>
                      <span>{assignee.name}</span>
                    </p>
                  ))}
              </div>
              {showAddAssigneeDialog && (
                <WorkFlowAddAssigneeDialog
                  handleClose={() => setShowAddAssigneeDialog(false)}
                  allOptions={userList}
                  selected={details.assignees.map((row) => row.id)}
                  handleSelection={handleAssigneeSelection}
                  title="Assignee"
                />
              )}
            </div>
          </div>
          <div className="followers-main-container flex-row gap-48 justify-content-space-between xetgo-font-tag">
            <p className="bold left-title">Followers</p>
            <div className="flex-row gap-8 flex-1">
              <img
                onClick={() => setShowAddFollowersDialog(true)}
                className="cursor-pointer"
                height={16}
                width={16}
                src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250403104959-user-plus.svg"
                alt="add-user-icon"
              />
              <div
                style={{ color: "#333333" }}
                className="flex-row gap-8 flex-wrap"
              >
                {details.followers &&
                  details.followers.map((follower, index) => (
                    <p
                      className="flex-row align-items-center justify-content-center gap-4"
                      key={index}
                    >
                      <span className="dot-follower p-1"> </span>
                      <span>{follower.name}</span>
                    </p>
                  ))}
              </div>
            </div>
            {showAddFollowersDialog && (
              <WorkFlowAddAssigneeDialog
                handleClose={() => setShowAddFollowersDialog(false)}
                allOptions={userList}
                selected={details.followers.map((row) => row.id)}
                handleSelection={handleFollowerSelection}
                title="Follower"
              />
            )}
          </div>
          <div className="mid-border"></div>
          <div className=" flex-row gap-48 justify-content-space-between xetgo-font-tag">
            <p className="bold left-title">Assigned date</p>
            <p style={{ color: "#333333" }} className="flex-1">
              {details.assigned_date}
            </p>
          </div>
          <div className=" flex-row gap-48 justify-content-space-between xetgo-font-tag">
            <p className="bold left-title">Due date</p>
            <p style={{ color: "#333333" }} className="flex-1">
              {details.due_date}
            </p>
          </div>
          <div className=" flex-row gap-48 justify-content-space-between xetgo-font-tag">
            <p className="bold left-title">Completed date</p>
            <p style={{ color: "#333333" }} className="flex-1">
              {details.completed_date ? details.completed_date : "N/A"}
            </p>
          </div>
          <div className=" flex-row gap-48 justify-content-space-between xetgo-font-tag">
            <p className="bold left-title">Notes</p>
            {noteEditing ? (
              <div
                ref={noteRef}
                contentEditable
                dangerouslySetInnerHTML={{ __html: details.notes }}
                className="note-editing px-8 py-6 border-box overflow-scroll"
                onKeyDown={handleKeyDown}
              ></div>
            ) : (
              <div className="note flex-row gap-12 flex-1 justify-content-space-between">
                <p style={{ color: "#333333" }}>
                  {details.notes || "Share a note"}
                </p>
                {details.status !== WORKFLOW_REQUEST_STAGE_STATUS.COMPLETED && (
                  <img
                    onClick={() => setNoteEditing(true)}
                    className="cursor-pointer"
                    height={12}
                    width={12}
                    src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20240908105528-Group+300.svg"
                    alt="edit-icon"
                  />
                )}
              </div>
            )}
          </div>
          {/* <div className="integration flex-column gap-16">
            <p className="bold">Integrations</p>
            <div className="all-integration-container flex-row gap-8 flex-wrap">
              {details.integrations &&
                details.integrations.map((integrate, index) => (
                  <div
                    key={index}
                    className="integration-container align-items-center flex-column gap-4 xetgo-font-mini px-15 py-6 border-box"
                  >
                    <img
                      height={22}
                      width={22}
                      src={`${integrate.avatar}`}
                      alt={`${integrate.name}-icon`}
                    />
                    <p className="bold">{integrate.name}</p>
                  </div>
                ))}
            </div>
          </div> */}
          {details.tasks.length > 0 && (
            <>
              <div className="mid-border"></div>
              <div className="flex-column gap-16">
                <p className="xetgo-font-tag bolder">
                  {details.tasks.length} Tasks
                </p>
                {details.tasks.map((task) => {
                  return (
                    <div
                      className="flex-row gap-8"
                      key={`stage-tasks-${task.id}`}
                    >
                      <img
                        src={
                          task.active
                            ? "https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250522104942-Check+Icon+%281%29.svg"
                            : "https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250522104942-Check+Icon.svg"
                        }
                        alt="checkbox"
                      />
                      <p
                        className="xetgo-font-tag"
                        style={{ color: "#333333" }}
                      >
                        {task.name}
                      </p>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          <div className="mid-border"></div>
          <div className="next-stage-main-container flex-row align-items-center  justify-content-space-between">
            {details.status === WORKFLOW_REQUEST_STAGE_STATUS.IN_PROGRESS && (
              <div className="stage-filter-main-container">
                <Filter
                  option={{
                    key: "vendor_form",
                    name: "Stage",
                  }}
                  optionList={details.next_stages || []}
                  multipleSelection={false}
                  onSelection={(val, _option) => {
                    handleNextStep(val);
                  }}
                  preSelected={[]}
                  mode="showNames"
                  showPlaceholder={true}
                  direction="left"
                  addNew={false}
                  classname="stage-filter-input"
                  blended={true}
                />
              </div>
            )}
            {details.status === WORKFLOW_REQUEST_STAGE_STATUS.IN_PROGRESS && (
              <div
                className={`current-status px-12 py-4 xetgo-font-tag cursor-pointer hold-btn`}
              >
                Hold
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default RequestStageDetail;
